// 📁 SERVICIO DE GESTIÓN DE CARPETAS

// Función para obtener configuración de email
const getEmailConfig = () => {
  const savedConfig = localStorage.getItem('emailConfig');
  if (!savedConfig) {
    throw new Error('No se encontró configuración de email. Configure su cuenta primero.');
  }
  return JSON.parse(savedConfig);
};

// 📂 CREAR CARPETA
export const createFolder = async (
  folderName: string,
  parentFolder?: string
): Promise<{ success: boolean; message: string; folderPath?: string }> => {
  try {
    const config = getEmailConfig();

    // 🔧 VALIDACIÓN Y LIMPIEZA DEL NOMBRE
    const cleanFolderName = folderName.trim();
    
    // Validaciones adicionales para nombres de carpetas IMAP
    if (!cleanFolderName) {
      return { success: false, message: 'El nombre de la carpeta no puede estar vacío' };
    }

    // Caracteres no permitidos en IMAP
    const invalidChars = /[\/\\:*?"<>|[\]]/;
    if (invalidChars.test(cleanFolderName)) {
      return { success: false, message: 'El nombre contiene caracteres no válidos. Evita: /\\:*?"<>|[]' };
    }

    // No permitir nombres que empiecen con punto o guión
    if (cleanFolderName.startsWith('.') || cleanFolderName.startsWith('-')) {
      return { success: false, message: 'El nombre no puede empezar con punto (.) o guión (-)' };
    }

    // No permitir espacios al inicio o final
    if (cleanFolderName !== cleanFolderName.trim()) {
      return { success: false, message: 'El nombre no puede tener espacios al inicio o final' };
    }

    // Longitud máxima para nombres de carpeta
    if (cleanFolderName.length > 50) {
      return { success: false, message: 'El nombre no puede exceder 50 caracteres' };
    }

    // 🔧 CONSTRUCCIÓN DEL PATH CORREGIDA
    let folderPath = cleanFolderName;
    
    if (parentFolder && parentFolder.trim()) {
      // Si hay carpeta padre, construir el path correctamente
      const cleanParent = parentFolder.trim();
      
      // Determinar el delimitador (por defecto '/')
      const delimiter = '/';
      
      // Construir el path completo
      folderPath = `${cleanParent}${delimiter}${cleanFolderName}`;
    }

    console.log('📁 Creating folder with path:', folderPath);

    const response = await fetch('https://backend.anubisai.net/api/pmail/create-folder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        server: config.server,
        username: config.username,
        password: config.password,
        imapPort: config.imapPort,
        ssl: config.ssl,
        folderName: cleanFolderName, // Solo el nombre, sin path
        parentFolder: parentFolder?.trim() || '' // Path de la carpeta padre
      })
    });

    const result = await response.json();

    if (response.ok && result.success) {
      return {
        success: true,
        message: result.message,
        folderPath: result.fullPath || folderPath
      };
    } else {
      // Mejorar mensajes de error del servidor
      let errorMessage = result.error || 'Error al crear carpeta';
      
      if (errorMessage.includes('invalid mailbox name')) {
        errorMessage = 'Nombre de carpeta no válido. Use solo letras, números y espacios.';
      } else if (errorMessage.includes('ALREADYEXISTS')) {
        errorMessage = 'Ya existe una carpeta con ese nombre.';
      } else if (errorMessage.includes('CREATE failed')) {
        errorMessage = 'Error del servidor al crear la carpeta. Verifique el nombre.';
      }
      
      return {
        success: false,
        message: errorMessage
      };
    }
  } catch (error: any) {
    console.error('Error creating folder:', error);
    return {
      success: false,
      message: `Error de conexión: ${error.message}`
    };
  }
};

// 🗑️ ELIMINAR CARPETA
export const deleteFolder = async (
  folderPath: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const config = getEmailConfig();

    const response = await fetch('https://backend.anubisai.net/api/pmail/delete-folder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        server: config.server,
        username: config.username,
        password: config.password,
        imapPort: config.imapPort,
        ssl: config.ssl,
        folderPath: decodeURIComponent(folderPath)
      })
    });

    const result = await response.json();

    if (response.ok && result.success) {
      return {
        success: true,
        message: result.message
      };
    } else {
      throw new Error(result.error || 'Error al eliminar carpeta');
    }
  } catch (error: any) {
    console.error('Error deleting folder:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

// ✏️ RENOMBRAR CARPETA
export const renameFolder = async (
  oldFolderPath: string,
  newFolderName: string
): Promise<{ success: boolean; message: string; newPath?: string }> => {
  try {
    const config = getEmailConfig();

    const response = await fetch('https://backend.anubisai.net/api/pmail/rename-folder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        server: config.server,
        username: config.username,
        password: config.password,
        imapPort: config.imapPort,
        ssl: config.ssl,
        oldFolderPath: decodeURIComponent(oldFolderPath),
        newFolderName: newFolderName.trim()
      })
    });

    const result = await response.json();

    if (response.ok && result.success) {
      return {
        success: true,
        message: result.message,
        newPath: result.newPath
      };
    } else {
      throw new Error(result.error || 'Error al renombrar carpeta');
    }
  } catch (error: any) {
    console.error('Error renaming folder:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

// 📊 UTILIDADES PARA VALIDACIÓN
export const validateFolderName = (name: string): { valid: boolean; error?: string } => {
  const trimmedName = name.trim();
  
  if (!trimmedName) {
    return { valid: false, error: 'El nombre de la carpeta no puede estar vacío' };
  }
  
  if (trimmedName.length > 50) {
    return { valid: false, error: 'El nombre no puede exceder 50 caracteres' };
  }
  
  // Caracteres prohibidos más estrictos para IMAP
  const invalidChars = /[\/\\:*?"<>|[\]]/;
  if (invalidChars.test(trimmedName)) {
    return { valid: false, error: 'Caracteres no válidos: /\\:*?"<>|[]' };
  }
  
  // No empezar con punto o guión
  if (trimmedName.startsWith('.') || trimmedName.startsWith('-')) {
    return { valid: false, error: 'No puede empezar con punto (.) o guión (-)' };
  }
  
  // Nombres reservados del sistema
  const systemFolders = ['INBOX', 'SENT', 'DRAFTS', 'TRASH', 'SPAM', 'ARCHIVE'];
  if (systemFolders.includes(trimmedName.toUpperCase())) {
    return { valid: false, error: 'No se pueden usar nombres reservados del sistema' };
  }
  
  // No permitir solo espacios o caracteres especiales
  if (!/[a-zA-Z0-9]/.test(trimmedName)) {
    return { valid: false, error: 'El nombre debe contener al menos una letra o número' };
  }
  
  return { valid: true };
};

export const isSystemFolder = (folderPath: string): boolean => {
  const systemFolders = ['INBOX', 'Sent', 'Drafts', 'Trash', 'Spam', 'Archive'];
  const folderNameLower = folderPath.toLowerCase();
  return systemFolders.some(folder => folderNameLower.includes(folder.toLowerCase()));
};
